// Läufe-Tabelle (Seite „Läufe“, Reiter im Editor) und der Verlauf eines Laufs. Eine Zeile je Lead im Workflow:
// Status, Stand, Zeit, Steuerung (Pausieren, Fortsetzen, Wiederholen, Beenden im Menü „…“ am Zeilenende). Klick auf die
// Zeile klappt den Verlauf der Schritte auf. Feste Spaltenbreiten: die Tabelle passt ab 1024 px ohne Quer-Scrollen,
// der Stand bricht um (bis zu drei Zeilen), der volle Text steht im Tooltip.
import { Fragment, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { LaufStatus, WorkflowLauf } from "../api/workflows";
import { StatusPille } from "../components/Bausteine";
import { Icon } from "../components/Icon";
import { MenueKnopf, type MenuePunkt } from "../components/Menue";
import { relativ, zeitpunktLang, zeitpunktText } from "../werkzeuge/zeit";
import { entwurfOeffnen } from "./entwurf";
import { LAUF_STATUS, schrittInfo } from "./hilfen";
import type { LaufMitZiel } from "./teil2Api";
import "./workflows2.css";

export type LaufAktion = "pausieren" | "fortsetzen" | "abbrechen";

const FARBE: Record<LaufStatus, string> = { wartet: "blue", laeuft: "teal", pausiert: "yellow", fertig: "green", fehler: "magenta", abgebrochen: "gray" };

export function LaufStatusPille({ status, ziel = false }: { status: LaufStatus; ziel?: boolean }) {
  // Ein Lauf, der sein Ziel erreicht hat, ist im Status „fertig“; die Pille sagt es dazu (Runde 10)
  return ziel && status === "fertig" ? <StatusPille label="Ziel erreicht" farbe="teal" /> : <StatusPille label={LAUF_STATUS[status]} farbe={FARBE[status]} />;
}

/** Eine Zeile Stand: wo steht der Lauf, was kommt als Nächstes. */
export function laufStand(l: WorkflowLauf): { text: string; fehler: boolean } {
  const letzter = l.ergebnisse[l.ergebnisse.length - 1];
  switch (l.status) {
    case "fertig": {
      const ziel = (l as LaufMitZiel).ziel_erreicht;
      if (ziel) return { text: `Ziel erreicht${ziel.ziel?.name ? `: ${ziel.ziel.name}` : ""} · ${Math.min(l.schritt_index, l.schritt_gesamt)} von ${l.schritt_gesamt} Schritten getan`, fehler: false };
      return { text: `Alle ${l.schritt_gesamt} ${l.schritt_gesamt === 1 ? "Schritt" : "Schritte"} erledigt`, fehler: false };
    }
    case "fehler":
      return { text: l.fehler ?? letzter?.text ?? "Fehler", fehler: true };
    case "abgebrochen":
      return { text: l.fehler ?? letzter?.text ?? "Von Hand beendet", fehler: false };
    case "pausiert":
      return { text: `Pausiert vor Schritt ${Math.min(l.schritt_index + 1, l.schritt_gesamt)} von ${l.schritt_gesamt}`, fehler: false };
    default: {
      if (letzter?.wartet_auf) return { text: "Wartet, bis die Pflichtaufgabe erledigt ist", fehler: false };
      if ((letzter?.status as string) === "verschoben" && letzter?.text) return { text: `Schritt ${l.schritt_index + 1} von ${l.schritt_gesamt}: ${letzter.text}`, fehler: false };
      const naechster = l.naechster_schritt ? l.naechster_schritt.name || schrittInfo(l.naechster_schritt.art).label : null;
      const wann = l.naechster_schritt_um ? ` · ${relativ(l.naechster_schritt_um)}` : "";
      return { text: naechster ? `Schritt ${l.schritt_index + 1} von ${l.schritt_gesamt}: ${naechster}${wann}` : "Wird bearbeitet", fehler: false };
    }
  }
}

function ErgebnisIcon({ status, art }: { status: string; art: string }) {
  if (art === "ziel") return <Icon name="erledigt" groesse={14} className="wf-erg__icon wf-erg__icon--ziel" />;
  if (status === "verschoben") return <Icon name="uhr" groesse={14} className="wf-erg__icon wf-erg__icon--leise" />;
  if (status === "fehler") return <Icon name="warnung" groesse={14} className="wf-erg__icon wf-erg__icon--fehler" />;
  if (status === "uebersprungen") return <Icon name="pfeil-rechts" groesse={14} className="wf-erg__icon wf-erg__icon--leise" />;
  return <Icon name="erledigt" groesse={14} className="wf-erg__icon wf-erg__icon--ok" />;
}

export function LaufVerlauf({ lauf }: { lauf: WorkflowLauf }) {
  const navigate = useNavigate();
  if (!lauf.ergebnisse.length) return <div className="leise wf-verlauf__leer">Noch kein Schritt ausgeführt.</div>;
  return (
    <ol className="wf-verlauf">
      {lauf.ergebnisse.map((e, i) => {
        const art = e.art as string;
        const titel = art === "ziel" ? "Ziel erreicht" : schrittInfo(e.art).label;
        const verschoben = (e.status as string) === "verschoben";
        return (
          <li key={i} className="wf-erg">
            <ErgebnisIcon status={e.status} art={art} />
            <div className="wf-erg__text">
              <div>
                <strong>{titel}</strong>
                {verschoben ? <span className="leise"> · verschoben ({schrittInfo(e.art).label})</span> : null}
                <span className="leise"> · {zeitpunktText(e.zeit)}</span>
              </div>
              <div className={e.status === "fehler" ? "text-gefahr" : undefined}>{e.text}</div>
              {e.entwurf ? (
                <button type="button" className="knopf knopf--klein wf-erg__entwurf" onClick={() => entwurfOeffnen(navigate, lauf.lead_id, e.entwurf!)}>
                  <Icon name={e.entwurf.art === "email" ? "mail" : "sms"} groesse={13} />
                  {e.entwurf.art === "email" ? "E-Mail-Entwurf öffnen" : "SMS-Entwurf öffnen"}
                </button>
              ) : null}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/** Was sich mit einem Lauf im Zustand `status` tun lässt, als Menüpunkte. */
export function laufMenue(lauf: WorkflowLauf, onAktion: (l: WorkflowLauf, a: LaufAktion) => void): MenuePunkt[] {
  const punkt = (a: LaufAktion, label: string, hinweis: string, gefahr = false): MenuePunkt => ({
    label,
    hinweis,
    gefahr,
    trenner: a === "abbrechen",
    onClick: () => onAktion(lauf, a),
  });
  switch (lauf.status) {
    case "wartet":
      return [punkt("pausieren", "Pausieren", "Der Lauf hält vor dem nächsten Schritt an"), punkt("abbrechen", "Beenden", "Der Lauf endet, die Schritte danach laufen nicht mehr", true)];
    case "pausiert":
      return [punkt("fortsetzen", "Fortsetzen", "Der Lauf macht mit dem nächsten Schritt weiter"), punkt("abbrechen", "Beenden", "Der Lauf endet, die Schritte danach laufen nicht mehr", true)];
    case "fehler":
      return [punkt("fortsetzen", "Wiederholen", "Den fehlgeschlagenen Schritt noch einmal versuchen"), punkt("abbrechen", "Beenden", "Der Lauf endet, die Schritte danach laufen nicht mehr", true)];
    default:
      return [];
  }
}

/** Steuerung eines Laufs: ein Menü „…“ (schmal, damit die Tabelle auch bei 1024 px passt). Leer, wenn nichts mehr zu tun ist. */
export function LaufAktionen({ lauf, onAktion }: { lauf: WorkflowLauf; onAktion: (l: WorkflowLauf, a: LaufAktion) => void }) {
  const punkte = laufMenue(lauf, onAktion);
  if (!punkte.length) return null;
  return (
    <MenueKnopf knopfKlasse="symbolknopf symbolknopf--klein" titel={`Lauf steuern: ${punkte.map((p) => p.label).join(", ")}`} ausrichtung="end" breite={200} punkte={punkte}>
      <Icon name="mehr" groesse={15} />
    </MenueKnopf>
  );
}

interface Props {
  laeufe: WorkflowLauf[];
  zeigeWorkflow?: boolean;
  zeigeLead?: boolean;
  kannVerwalten: boolean;
  onAktion: (l: WorkflowLauf, a: LaufAktion) => void;
}

export function LaeufeTabelle({ laeufe, zeigeWorkflow = true, zeigeLead = true, kannVerwalten, onAktion }: Props) {
  const [offen, setOffen] = useState<string | null>(null);
  return (
    <div className="tabelle-rahmen wf-tabelle-rahmen">
      <table className="tabelle wf-laeufe">
        <thead>
          <tr>
            {zeigeLead ? <th className="wf-spalte-lead">Lead</th> : null}
            {zeigeWorkflow ? <th className="wf-spalte-wf">Workflow</th> : null}
            <th className="wf-spalte-status">Status</th>
            <th className="wf-spalte-stand">Stand</th>
            <th className="wf-spalte-zeit">Gestartet</th>
            {kannVerwalten ? <th className="wf-spalte-aktion" aria-label="Aktionen" /> : null}
          </tr>
        </thead>
        <tbody>
          {laeufe.map((l) => {
            const stand = laufStand(l);
            const auf = offen === l.id;
            return (
              <Fragment key={l.id}>
                <tr className={auf ? "tabelle__zeile--fokus" : undefined} onClick={() => setOffen(auf ? null : l.id)} aria-expanded={auf}>
                  {zeigeLead ? (
                    <td className="wf-zelle-lead">
                      <Link to={`/lead/${encodeURIComponent(l.lead_id)}`} onClick={(e) => e.stopPropagation()} className="wf-lauf-link">
                        {l.lead_name ?? l.lead_id}
                      </Link>
                    </td>
                  ) : null}
                  {zeigeWorkflow ? (
                    <td className="wf-zelle-wf">
                      <Link to={`/workflows/${encodeURIComponent(l.workflow_id)}`} onClick={(e) => e.stopPropagation()} className="wf-lauf-link">
                        {l.workflow_name ?? l.workflow_id}
                      </Link>
                    </td>
                  ) : null}
                  <td className="wf-spalte-status">
                    <LaufStatusPille status={l.status} ziel={!!(l as LaufMitZiel).ziel_erreicht} />
                  </td>
                  <td className={`wf-spalte-stand${stand.fehler ? " text-gefahr" : ""}`} title={stand.text}>
                    <span className="wf-stand">{stand.text}</span>
                  </td>
                  <td title={zeitpunktLang(l.gestartet_at)} className="wf-spalte-zeit leise">
                    {relativ(l.gestartet_at)}
                  </td>
                  {kannVerwalten ? (
                    <td className="wf-zelle-aktion wf-spalte-aktion">
                      <LaufAktionen lauf={l} onAktion={onAktion} />
                    </td>
                  ) : null}
                </tr>
                {auf ? (
                  <tr className="wf-verlauf-zeile">
                    <td colSpan={(zeigeLead ? 1 : 0) + (zeigeWorkflow ? 1 : 0) + 3 + (kannVerwalten ? 1 : 0)}>
                      <LaufVerlauf lauf={l} />
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
